import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from "react";
import { AdSlot, DFPManager, DFPSlotsProvider } from "react-dfp";
import { connect } from "react-redux";
import { withError } from "./with-error";
/**
 * This is a function which can be used to manage ad units in a single place. A component must be created, and used with the `adtype` parameter. These ads are lazy-loaded and single-request mode is disabled by default which can be overwritten as follows.
 *
 * Example
 * ```javascript
 * import { createDfpAdComponent } from '@quintype/components';
 *
 * export const CONFIG = {
 *   "homepage-2": { adUnit: "HP_728x90-3", sizes: [[728, 90], [320, 50]] },
 *   "homepage-3": { adUnit: "HP_728x90-3", sizes: [[728, 90], [320, 50]] },
 * }
 *
 * export const DfpAd = createDfpAdComponent({
 *   defaultNetworkID: "123456789",
 *   config: CONFIG,
 *   targeting: function(state, ownProps) {
 *     const params = {};
 *
 *     // if(storyIsSponsored) params['sponsor'] = storySponsor
 *
 *     return params;
 *   },
 *   // Only if you want to overwrite the existing values
 *   lazyLoad: false,
 *   singleRequest: true
 * });
 *
 * <DfpAd adtype="homepage-2" />
 * ```
 * @param {Object} params
 * @param {string} params.defaultNetworkID - Network Id of the Ad Provider
 * @param {Object} params.config - Configuration of the ads (see example)
 * @param {string} params.slotId - A unique string identify the Ad slot
 * @param {function} params.targeting - Function which takes in the current state, props from the parent component and returns targetting parameters
 * @param {boolean} params.collapseEmptyDivs (default true)
 * @param {boolean} params.lazyLoad (default true)
 * @param {boolean} params.singleRequest - Run Dfp in Single Request Mode (default false)
 * @param {number} params.deferAdsBy - Defers loading of gpt.js script by the passed value in ms
 * @category Ads
 * @returns {Component} A component that can
 */

export function createDfpAdComponent(_ref) {
  var defaultNetworkID = _ref.defaultNetworkID,
      _ref$config = _ref.config,
      config = _ref$config === void 0 ? {} : _ref$config,
      slotId = _ref.slotId,
      targeting = _ref.targeting,
      _ref$collapseEmptyDiv = _ref.collapseEmptyDivs,
      collapseEmptyDivs = _ref$collapseEmptyDiv === void 0 ? true : _ref$collapseEmptyDiv,
      _ref$lazyLoad = _ref.lazyLoad,
      lazyLoad = _ref$lazyLoad === void 0 ? true : _ref$lazyLoad,
      _ref$singleRequest = _ref.singleRequest,
      singleRequest = _ref$singleRequest === void 0 ? false : _ref$singleRequest,
      _ref$deferAdsBy = _ref.deferAdsBy,
      deferAdsBy = _ref$deferAdsBy === void 0 ? 0 : _ref$deferAdsBy;
  return connect(function (state, ownProps) {
    return {
      targetingArguments: targeting(state, ownProps),
      defaultNetworkID: defaultNetworkID,
      config: config,
      slotId: slotId,
      collapseEmptyDivs: collapseEmptyDivs,
      lazyLoad: lazyLoad,
      singleRequest: singleRequest,
      deferAdsBy: deferAdsBy
    };
  }, function () {
    return {};
  })(withError(DfpAdBase));
}

function DfpAdBase(_ref2) {
  var defaultNetworkID = _ref2.defaultNetworkID,
      config = _ref2.config,
      slotId = _ref2.slotId,
      collapseEmptyDivs = _ref2.collapseEmptyDivs,
      targetingArguments = _ref2.targetingArguments,
      adtype = _ref2.adtype,
      lazyLoad = _ref2.lazyLoad,
      singleRequest = _ref2.singleRequest,
      deferAdsBy = _ref2.deferAdsBy;
  var adConfig = config[adtype] || {};

  var adProps = _objectSpread({
    slotId: slotId
  }, adConfig);

  return /*#__PURE__*/React.createElement(DFPSlotsProvider, {
    dfpNetworkId: defaultNetworkID,
    collapseEmptyDivs: collapseEmptyDivs,
    targetingArguments: targetingArguments,
    sizeMapping: adConfig.viewPortSizeMapping,
    lazyLoad: lazyLoad,
    singleRequest: singleRequest,
    deferAdsBy: deferAdsBy
  }, /*#__PURE__*/React.createElement(AdSlot, adProps));
}

export function refreshDfpAds(adSlots) {
  DFPManager.refresh(adSlots);
}